// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import clearSearch from "@stanion/App/Handlers/AllEvents/ClearSearch";
import loadEventLocations from "@stanion/App/Handlers/AllEvents/EventLocationHandler";
import loadEventTypes from "@stanion/App/Handlers/AllEvents/EventTypesHandler";
import updateSearchFields from "@stanion/App/Handlers/AllEvents/UpdateSearchFields";
import { CustomAppState } from "@stanion/App/Store/Reducers";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    parameter: state.xAllEventsPage.getAllEventsParameter,
});

const mapDispatchToProps = {
    clearSearch,
    updateSearchFields,
    loadEventTypes,
    loadEventLocations,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface PromotionFileRequestStyles {
    container?: InjectableCss;
    selectFieldContainer?: InjectableCss;
    buttonWrapper?: InjectableCss;
    selectField?: SelectPresentationProps;
    clearButton?: ButtonPresentationProps;
}

const promotionFileRequestStyles: PromotionFileRequestStyles = {
    container: {
        css: css`
            display: flex;
            align-items: flex-end;
        `,
    },
    selectFieldContainer: {
        css: css`
            margin-right: 15px;
        `,
    },
};

const styles = promotionFileRequestStyles;

const AllEventsActions: FC<Props> = ({
    parameter,
    clearSearch,
    updateSearchFields,
    loadEventTypes,
    loadEventLocations,
}) => {
    const [location, setLocation] = useState<string>("");
    const [eventType, setEventType] = useState<string>("");
    const [locations, setLocations] = useState<string[]>([]);
    const [eventTypes, setEventTypes] = useState<string[]>([]);

    useEffect(() => {
        loadEventTypes({
            onSuccess(result) {
                setEventTypes([...result]);
            },
        });

        loadEventLocations({
            onSuccess(result) {
                setLocations([...result]);
            },
        });
    }, []);

    const locationChangeHandler = (event: React.FormEvent<HTMLSelectElement>) => {
        loadEventTypes({
            location: event.currentTarget.value,
            onSuccess(result) {
                setEventTypes(result);
            },
        });
        setLocation(event.currentTarget.value);
        updateSearchFields({ ...parameter, location: event.currentTarget.value });
    };

    const eventTypeChangeHandler = (event: React.FormEvent<HTMLSelectElement>) => {
        setEventType(event.currentTarget.value);
        updateSearchFields({ ...parameter, eventType: event.currentTarget.value });
    };

    const clearFilterHandler = () => {
        setLocation("");
        setEventType("");
        clearSearch();
    };

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.selectFieldContainer}>
                <Select
                    label={translate("Location")}
                    onChange={locationChangeHandler}
                    value={location}
                    {...styles.selectField}
                >
                    <option value="">{translate("Select Location")}</option>
                    {locations &&
                        locations?.map((value: string, index) => (
                            <option key={index} value={value}>
                                {value}
                            </option>
                        ))}
                </Select>
            </StyledWrapper>
            <StyledWrapper {...styles.selectFieldContainer}>
                <Select
                    label={translate("Event Type")}
                    onChange={eventTypeChangeHandler}
                    value={eventType}
                    {...styles.selectField}
                >
                    <option value="">{translate("Select Event Type")}</option>
                    {eventTypes &&
                        eventTypes?.map((value: string, index) => (
                            <option key={index} value={value}>
                                {value}
                            </option>
                        ))}
                </Select>
            </StyledWrapper>
            <StyledWrapper {...styles.buttonWrapper}>
                <Button
                    {...styles.clearButton}
                    data-test-selector="AllEvents_clearFilters"
                    onClick={clearFilterHandler}
                >
                    {translate("Clear Filters")}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AllEventsActions),
    definition: {
        displayName: "All Events Actions",
        allowedContexts: ["AllEventsPage" as any],
        group: "All Events" as any,
    },
};

export default widgetModule;
